import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { useState, useEffect } from 'react';
import { START_DATE, useDatepicker } from '@datepicker-react/hooks';
import DatepickerContext from './context.js';
import Month from './Month.js';
import { DatepickerStyle, DatepickerInputsContainerStyle, MonthsContainerStyle } from './styles.js';
import { getMaxBookingDate, formatDate } from './utils.js';
import Button from '../Button/Button.js';
import { Input } from '../Input/Input.js';
import '../Input/InputBody.js';
import '@olist/design-system-icons';
import '../Input/InputPassword/styles.js';
import enUS from './../../external/date-fns/esm/locale/en-US/index.js';

function Datepicker(_a) {
    var { clearLabel = 'clear dates', onClear, onChange, locale = enUS, endDateLabel = 'until', startDateLabel = 'from', maxBookingDays = 30, minBookingDate = new Date('2019-01-01T03:00:00.000Z'), shouldResetOnSubmit = false, submit, submitLabel = 'select dates', defaultValues = {
        startDate: null,
        endDate: null,
    }, values = {
        startDate: null,
        endDate: null,
    }, showSubmitButton = true, inContainer = false } = _a, props = __rest(_a, ["clearLabel", "onClear", "onChange", "locale", "endDateLabel", "startDateLabel", "maxBookingDays", "minBookingDate", "shouldResetOnSubmit", "submit", "submitLabel", "defaultValues", "values", "showSubmitButton", "inContainer"]);
    const isValidValues = values.startDate instanceof Date || values.startDate === '';
    const [state, setState] = useState(Object.assign(Object.assign(Object.assign({}, defaultValues), (isValidValues && values)), { focusedInput: START_DATE }));
    const { startDate, endDate, focusedInput } = state;
    const shouldDisableButton = !startDate || !endDate;
    const handleDateChange = (data) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(data);
        return (data === null || data === void 0 ? void 0 : data.focusedInput)
            ? setState(data)
            : setState(Object.assign(Object.assign({}, data), { focusedInput: START_DATE }));
    };
    const { firstDayOfWeek, activeMonths, isDateSelected, isDateHovered, isFirstOrLastSelectedDate, isDateBlocked, isDateFocused, focusedDate, onDateHover, onDateSelect, onDateFocus, onResetDates, goToPreviousMonths, goToNextMonths, } = useDatepicker({
        endDate,
        firstDayOfWeek: 0,
        focusedInput: focusedInput,
        maxBookingDate: getMaxBookingDate(startDate, maxBookingDays),
        minBookingDate,
        numberOfMonths: 1,
        startDate,
        onDatesChange: handleDateChange,
    });
    const handleSubmit = () => {
        if (submit)
            submit(startDate, endDate);
        if (shouldResetOnSubmit) {
            onResetDates();
        }
    };
    const handleClear = () => {
        onClear === null || onClear === void 0 ? void 0 : onClear();
        setState(Object.assign(Object.assign({}, state), { endDate: null, startDate: null }));
    };
    useEffect(() => {
        if (isValidValues)
            setState(Object.assign(Object.assign({}, state), values));
    }, [values]);
    return (React.createElement(DatepickerContext.Provider, { value: {
            focusedDate,
            locale,
            isDateFocused,
            isDateSelected,
            isDateHovered,
            isDateBlocked,
            isFirstOrLastSelectedDate,
            onDateSelect,
            onDateFocus,
            onDateHover,
        } },
        React.createElement(DatepickerStyle, Object.assign({ flexDirection: "column", justifyContent: "center", inContainer: inContainer }, props),
            React.createElement(DatepickerInputsContainerStyle, null,
                React.createElement("div", null,
                    React.createElement("div", null, startDateLabel),
                    React.createElement(Input, { type: "text", value: formatDate(startDate, locale), disabled: true, "data-ref": "start-date" })),
                React.createElement("div", null,
                    React.createElement("div", null, endDateLabel),
                    React.createElement(Input, { type: "text", value: formatDate(endDate, locale), disabled: true, "data-ref": "end-date" }))),
            React.createElement(MonthsContainerStyle, null, activeMonths.map(({ month, year }) => (React.createElement(Month, { firstDayOfWeek: firstDayOfWeek, key: `${year}-${month}`, month: month, year: year, goToNextMonths: goToNextMonths, goToPreviousMonths: goToPreviousMonths })))),
            showSubmitButton && (React.createElement(Button, { "data-ref": "submit", disabled: shouldDisableButton, fixed: true, variation: "primary", type: "button", onClick: handleSubmit }, submitLabel)),
            !!onClear && (React.createElement(Button, { mt: 8, fixed: true, type: "button", onClick: handleClear, variation: "secondary" }, clearLabel)))));
}

export { Datepicker as default };
